import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {Course, EducationCenter, EducationCenterCategory, User} from '../../../../shared/models';
import {CourseService, EducationCenterService} from '../../../../shared/services';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {CourseUtil, SeoUtil, WindowUtil} from '../../../../shared/utils';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {AuthService} from '../../../../shared/modules/auth/auth.service';
import {environment} from '../../../../../environments/environment';
import {FilePipe} from '../../../../shared/pipes/file/file.pipe';

declare var MainJs: any;

@Component({
  selector: 'app-center',
  templateUrl: './center.component.html'
})
export class CenterComponent implements OnInit, OnDestroy {
  readonly pageSize: number = 9;

  user: User;

  withPointsCount: number;
  withoutPointsCount: number;

  center: EducationCenter;
  courses: Array<Course> = [];
  totalPages: number = 0;
  selectedPage: number = 1;
  selectedPoints: Array<string> = [];
  selectedCategories: Array<string> = [];

  sponsorYouTubeUrl: SafeResourceUrl;

  loading: boolean;

  private destroy$ = new Subject();

  constructor(public courseUtil: CourseUtil,
              private windowUtil: WindowUtil,
              private sanitizer: DomSanitizer,
              private route: ActivatedRoute,
              private router: Router,
              private cd: ChangeDetectorRef,
              private filePipe: FilePipe,
              private educationCenterService: EducationCenterService,
              private courseService: CourseService,
              private authService: AuthService,
              private seoUtil: SeoUtil) {
  }

  ngOnInit(): void {
    this.mapQueryParams(this.route.snapshot.queryParams);
    this.handleRouteChange();
    this.handleUserChange();
  }

  ngOnDestroy(): void {
    this.seoUtil.setDefaultMetaTags();
    this.seoUtil.setDefaultTitle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCategory(category: EducationCenterCategory): void {
    let categoryIndex = this.selectedCategories.findIndex(categoryId => +categoryId === category.id);
    if (categoryIndex > -1) {
      this.selectedCategories.splice(categoryIndex, 1);
    } else {
      this.selectedCategories.push(category.id.toString());
    }
    this.updateRoute();
  }

  isCategoryChecked(category: EducationCenterCategory): boolean {
    return this.selectedCategories.indexOf(category.id.toString()) > -1;
  }

  togglePoints(points: number): void {
    let pointsIndex = this.selectedPoints.findIndex(point => +point === points);
    if (pointsIndex > -1) {
      this.selectedPoints.splice(pointsIndex, 1);
    } else {
      this.selectedPoints.push(points.toString());
    }
    this.updateRoute();
  }

  isPointsChecked(points: number): boolean {
    return this.selectedPoints.indexOf(points.toString()) > -1;
  }

  isMediaImage(url: string): boolean {
    if (!url) {
      return false;
    }
    return url.match(/\.(jpeg|jpg|gif|png)$/) != null;
  }

  private handleUserChange() {
    if (this.authService.isLoggedIn()) {
      this.authService.getAuthUser().subscribe(user => {
        this.user = user;
      });
    } else {
      this.user = null;
    }
    this.authService.userChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }

  private updateRoute(): void {
    let queryParams = {
      page: undefined,
      points: undefined,
      categories: undefined,
    };
    if (this.selectedPoints && this.selectedPoints.length > 0) {
      queryParams['points'] = this.selectedPoints.join(',');
    }
    if (this.selectedCategories && this.selectedCategories.length > 0) {
      queryParams['categories'] = this.selectedCategories.join(',');
    }
    this.router.navigate([], {
      relativeTo: this.route, queryParamsHandling: 'merge',
      queryParams: queryParams
    }).finally();
  }

  private fetchFilterData(): void {
    let courseIds = this.centerCourseIds();
    forkJoin([
      this.courseService.getCourses(1, 0, {minPoints: '0.0001', maxPoints: null, ids: courseIds.join(',')}),
      this.courseService.getCourses(1, 0, {minPoints: '0', maxPoints: '0', ids: courseIds.join(',')})
    ]).subscribe(([withPoints, withoutPoints]) => {
      this.withPointsCount = withPoints.total_count;
      this.withoutPointsCount = withoutPoints.total_count;
    });
  }

  private fetchEducationCenter(id: string): void {
    this.center = null;
    this.educationCenterService.getByName(id).subscribe(center => {
      this.center = center;
      this.sponsorYouTubeUrl = this.sanitizeYouTubeUrl(center.sponsor_media);
      this.cd.detectChanges();
      MainJs.collapsible();
      this.fetchFilterData();
      this.fetchCourses(this.selectedPage > 1 || this.selectedPoints.length > 0 || this.selectedCategories.length > 0);
      this.seoUtil.updateTitle(center.name + (center.description ? ' - ' + center.description : ''), false);
      this.seoUtil.updateMetaTags([
        {property: 'og:title', content: center.name},
        {property: 'og:description', content: center.description},
        {property: 'og:image', content: this.filePipe.transform(center.logo_image || center.banner_image, 'image', 'local')},
        {property: 'og:url', content: environment.sites.root + 'courses/centers/' + center.short_name}
      ]);
    }, () => {
      this.windowUtil.enableScrolling();
      this.router.navigate(['/not-found'], {skipLocationChange: true}).finally();
    });
  }

  private fetchCourses(scrollToCourses: boolean = false): void {
    let minMaxPoints = this.courseUtil.minMaxPoints(this.selectedPoints);
    let courseIds = this.centerCourseIds();
    this.loading = true;
    this.courseService.getCourses(this.selectedPage, this.pageSize, {
      minPoints: minMaxPoints[0],
      maxPoints: minMaxPoints[1],
      ids: courseIds.join(',')
    }).subscribe(resp => {
      this.courses = resp.data;
      this.totalPages = resp.total_pages;
      this.loading = false;
      this.cd.detectChanges();
      MainJs.progress();
      this.windowUtil.enableScrolling();
      if (scrollToCourses) {
        this.windowUtil.scrollTo('hero-container', 70);
      }
    }, () => {
      this.windowUtil.enableScrolling();
    });
  }

  private sanitizeYouTubeUrl(url: string): SafeResourceUrl {
    if (!url || this.isMediaImage(url)) {
      return null;
    }
    let videoId = url.split('v=')[1];
    let ampersandPosition = videoId.indexOf('&');
    if (ampersandPosition != -1) {
      videoId = videoId.substring(0, ampersandPosition);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl('https://www.youtube.com/embed/' + videoId);
  }

  private centerCourseIds(): Array<string> {
    let courseIds: Array<string>;
    if (this.selectedCategories.length > 0) {
      courseIds = this.selectedCategories.map(cid =>
        this.center.categories.find(c => c.id === +cid).course_ids.split(',')
      ).reduce((flat, next) => flat.concat(next), [])
    } else {
      courseIds = this.center.categories.map(c => c.course_ids.split(','))
        .reduce((flat, next) => flat.concat(next), [])
    }
    return courseIds;
  }

  private loadPage(scrollToCourses: boolean = false): void {
    if (this.selectedPage < 0) {
      return;
    }
    this.fetchCourses(scrollToCourses);
  }

  private handleRouteChange(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        let id = params['id'];
        if (id) {
          this.fetchEducationCenter(id);
        }
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(query => {
        this.windowUtil.disableScrolling();
        this.mapQueryParams(query);
        if (this.center) {
          this.loadPage(true);
        }
      });
  }

  private mapQueryParams(paramMap: Params): void {
    this.selectedPage = paramMap['page'] ? +paramMap['page'] : 1;
    this.selectedPoints = paramMap['points'] ? paramMap['points'].split(',') : [];
    this.selectedCategories = paramMap['categories'] ? paramMap['categories'].split(',') : [];
  }

}
